import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { Eye, EyeOff, LayoutDashboard, ArrowUp, ArrowDown, RotateCcw, GripVertical } from 'lucide-react';

// Master registry of toggle-able dashboard widgets, grouped for the panel.
export const WIDGET_GROUPS = [
  {
    group: 'Overview & Alerts',
    items: [
      { id: 'stats-cards', label: 'Stats Cards' },
      { id: 'renewal-steps', label: 'Visa Renewal Steps' },
      { id: 'active-probation', label: 'Active Probation Staff' },
      { id: 'missed-clockout', label: 'Missed Clock-Out Alerts' },
      { id: 'locked-accounts', label: 'Locked Accounts' },
      { id: 'security-log', label: 'Security Log' },
      { id: 'company-documents', label: 'Company Documents' },
      { id: 'dct-permit', label: 'DCT Permit Alerts' },
    ],
  },
  {
    group: 'Workforce Charts',
    items: [
      { id: 'nationality', label: 'Nationality Distribution' },
      { id: 'gender', label: 'Gender Distribution' },
      { id: 'department', label: 'Department Distribution' },
      { id: 'employment-status', label: 'Employment Status' },
      { id: 'visa-company', label: 'Visa Company Distribution' },
    ],
  },
  {
    group: 'Payroll',
    items: [
      { id: 'salary-cost', label: 'Monthly Salary Cost' },
      { id: 'payroll-history', label: 'Payroll History' },
      { id: 'payroll-comparison', label: 'Payroll Comparison' },
      { id: 'payroll-company', label: 'Payroll by Company' },
    ],
  },
  {
    group: 'Performance & Leaves',
    items: [
      { id: 'kpi', label: 'KPI Performance' },
      { id: 'leave-warnings', label: 'Leave Warnings' },
      { id: 'current-leaves', label: 'Current Leaves' },
      { id: 'expiring-docs', label: 'Expiring Documents' },
      { id: 'expired-docs', label: 'Expired Documents' },
      { id: 'upcoming-leaves', label: 'Upcoming Leaves' },
      { id: 'birthdays', label: 'Upcoming Birthdays' },
    ],
  },
  {
    group: 'Employee Status',
    items: [
      { id: 'suspended', label: 'Suspended Employees' },
      { id: 'warnings', label: 'Warning Letters' },
      { id: 'resigned', label: 'Resigned Employees' },
      { id: 'terminated', label: 'Terminated Employees' },
      { id: 'exit-comparison', label: 'Resignations vs Terminations (YoY)' },
    ],
  },
];

const DashboardCustomizeDialog = ({
  open, onOpenChange, hiddenWidgets = [], onToggleWidget,
  blockOrder = [], blockLabels = {}, onMoveBlock, onResetBlockOrder,
}) => {
  const isHidden = (id) => hiddenWidgets.includes(id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dashboard-customize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-violet-600" />
            Customize Dashboard
          </DialogTitle>
          <DialogDescription>
            Reorder sections with the arrows, and toggle which sections appear. Changes are saved automatically.
          </DialogDescription>
        </DialogHeader>

        {/* Section order */}
        {blockOrder.length > 0 && (
          <div className="mt-2" data-testid="dashboard-section-order">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Section Order</h4>
              <button
                type="button"
                onClick={onResetBlockOrder}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-violet-600 transition-colors"
                data-testid="reset-section-order"
              >
                <RotateCcw className="h-3 w-3" /> Reset order
              </button>
            </div>
            <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
              {blockOrder.map((id, idx) => (
                <div
                  key={id}
                  className="flex items-center justify-between gap-3 bg-white px-3 py-2 hover:bg-violet-50/40"
                  data-testid={`section-order-row-${id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <GripVertical className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                    <span className="text-[11px] text-gray-400 w-5 tabular-nums">{idx + 1}.</span>
                    <span className="text-sm text-gray-700 truncate">{blockLabels[id] || id}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => onMoveBlock(id, 'up')}
                      disabled={idx === 0}
                      className="p-1.5 rounded-md text-gray-500 hover:bg-violet-100 hover:text-violet-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                      title="Move up"
                      data-testid={`section-up-${id}`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onMoveBlock(id, 'down')}
                      disabled={idx === blockOrder.length - 1}
                      className="p-1.5 rounded-md text-gray-500 hover:bg-violet-100 hover:text-violet-700 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                      title="Move down"
                      data-testid={`section-down-${id}`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Separator className="mt-4" />
          </div>
        )}

        <div className="space-y-5 mt-2">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 -mb-1">Show / Hide Widgets</h4>
          {WIDGET_GROUPS.map((g) => (
            <div key={g.group}>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{g.group}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {g.items.map((w) => {
                  const visible = !isHidden(w.id);
                  return (
                    <div
                      key={w.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {visible
                          ? <Eye className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          : <EyeOff className="h-3.5 w-3.5 text-gray-400 shrink-0" />}
                        <span className={`text-sm truncate ${visible ? 'text-gray-700' : 'text-gray-400'}`}>{w.label}</span>
                      </div>
                      <Switch
                        checked={visible}
                        onCheckedChange={() => onToggleWidget(w.id)}
                        data-testid={`widget-toggle-${w.id}`}
                      />
                    </div>
                  );
                })}
              </div>
              <Separator className="mt-4" />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardCustomizeDialog;
